package com.terapia.terasenior.ui.patient

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.terapia.terasenior.domain.model.patient.PatientStatus
import com.terapia.terasenior.util.DateUtils

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun CreatePatientDialog(
    onDismiss: () -> Unit,
    onConfirm: (
        firstName: String, 
        lastName: String, 
        preferredName: String, 
        birthDate: String?, 
        externalId: String, 
        nif: String,
        admissionDate: String?,
        address: String,
        city: String,
        postalCode: String,
        province: String,
        phone: String,
        contactName: String,
        contactPhone: String,
        notes: String,
        status: PatientStatus
    ) -> Unit,
    isLoading: Boolean,
    errorMessage: String? = null
) {
    val layout = com.terapia.terasenior.ui.components.LocalTeraseniorLayout.current
    var firstName by remember { mutableStateOf("") }
    var lastName by remember { mutableStateOf("") }
    var preferredName by remember { mutableStateOf("") }
    var birthDate by remember { mutableStateOf("") }
    var externalId by remember { mutableStateOf("") }
    var nif by remember { mutableStateOf("") }
    var admissionDate by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var postalCode by remember { mutableStateOf("") }
    var province by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var contactName by remember { mutableStateOf("") }
    var contactPhone by remember { mutableStateOf("") }
    var notes by remember { mutableStateOf("") }
    var status by remember { mutableStateOf(PatientStatus.ACTIVE) }

    var selectedTab by remember { mutableStateOf(0) }

    AlertDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier
            .fillMaxWidth(if (layout.isCompactWidth) 0.96f else 0.9f)
            .widthIn(max = 760.dp)
            .heightIn(max = if (layout.isCompactHeight) 620.dp else 720.dp),
        properties = DialogProperties(usePlatformDefaultWidth = false),
        title = { Text("Alta de Nuevo Paciente", style = MaterialTheme.typography.headlineSmall) },
        text = {
            Column(modifier = Modifier.fillMaxWidth().heightIn(max = if (layout.isCompactHeight) 430.dp else 520.dp)) {
                if (errorMessage != null) {
                    Card(colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer), modifier = Modifier.padding(bottom = 12.dp)) {
                        Text(text = errorMessage, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(12.dp))
                    }
                }

                TabRow(selectedTabIndex = selectedTab, modifier = Modifier.padding(bottom = 16.dp)) {
                    Tab(selected = selectedTab == 0, onClick = { selectedTab = 0 }, text = { Text("Admin") })
                    Tab(selected = selectedTab == 1, onClick = { selectedTab = 1 }, text = { Text("Personal") })
                    Tab(selected = selectedTab == 2, onClick = { selectedTab = 2 }, text = { Text("Contacto") })
                    Tab(selected = selectedTab == 3, onClick = { selectedTab = 3 }, text = { Text("Notas") })
                }

                Column(modifier = Modifier.weight(1f).verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    when (selectedTab) {
                        0 -> {
                            OutlinedTextField(value = externalId, onValueChange = { externalId = it }, label = { Text("Nº Expediente / ID Interno") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = nif, onValueChange = { nif = it }, label = { Text("DNI / NIF") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = admissionDate, onValueChange = { admissionDate = it }, label = { Text("Fecha Alta (DD-MM-AAAA)") }, placeholder = { Text("Ej: 08-08-2026") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            
                            Text("Estado Inicial", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                PatientStatus.entries.filter { it != PatientStatus.BAJA }.forEach { s ->
                                    val label = when(s) {
                                        PatientStatus.ACTIVE -> "Activo"
                                        PatientStatus.INACTIVE -> "Inactivo"
                                        PatientStatus.DISCHARGED -> "Alta"
                                        else -> ""
                                    }
                                    if (label.isNotEmpty()) {
                                        FilterChip(selected = status == s, onClick = { status = s }, label = { Text(label) })
                                    }
                                }
                            }
                        }
                        1 -> {
                            OutlinedTextField(value = firstName, onValueChange = { firstName = it }, label = { Text("Nombre *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next), isError = firstName.isBlank())
                            OutlinedTextField(value = lastName, onValueChange = { lastName = it }, label = { Text("Apellidos *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next), isError = lastName.isBlank())
                            OutlinedTextField(value = preferredName, onValueChange = { preferredName = it }, label = { Text("Nombre Preferido") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = birthDate, onValueChange = { birthDate = it }, label = { Text("Fecha Nacimiento (DD-MM-AAAA)") }, placeholder = { Text("Ej: 20-05-1945") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                        }
                        2 -> {
                            OutlinedTextField(value = address, onValueChange = { address = it }, label = { Text("Dirección") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedTextField(value = city, onValueChange = { city = it }, label = { Text("Población") }, modifier = Modifier.weight(1f), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                                OutlinedTextField(value = postalCode, onValueChange = { postalCode = it }, label = { Text("CP") }, modifier = Modifier.width(100.dp), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            }
                            OutlinedTextField(value = province, onValueChange = { province = it }, label = { Text("Provincia") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = phone, onValueChange = { phone = it }, label = { Text("Teléfono Paciente") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            
                            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                            Text("Persona de Referencia", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                            OutlinedTextField(value = contactName, onValueChange = { contactName = it }, label = { Text("Nombre Referente") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = contactPhone, onValueChange = { contactPhone = it }, label = { Text("Teléfono Referente") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                        }
                        3 -> {
                            Text("Información Clínica y Notas", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                            OutlinedTextField(
                                value = notes, 
                                onValueChange = { notes = it }, 
                                label = { Text("Observaciones del Terapeuta") }, 
                                modifier = Modifier.fillMaxWidth().height(200.dp),
                                shape = RoundedCornerShape(12.dp)
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { 
                    onConfirm(
                        firstName, lastName, preferredName, 
                        DateUtils.toDbFormat(birthDate), 
                        externalId, nif, 
                        DateUtils.toDbFormat(admissionDate), 
                        address, city, postalCode, province, phone, contactName, contactPhone, notes, status
                    ) 
                },
                enabled = firstName.isNotBlank() && lastName.isNotBlank() && !isLoading,
                shape = RoundedCornerShape(12.dp)
            ) {
                if (isLoading) CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp) else Text("Finalizar Alta")
            }
        },
        dismissButton = { TextButton(onClick = onDismiss, enabled = !isLoading) { Text("Cancelar") } },
        shape = RoundedCornerShape(24.dp)
    )
}
